import { Heading } from 'react-aria-components';
import { Info, Mail, Navigation, Phone } from 'react-feather';
import { Link } from 'react-router-dom';

import StatusBadge from './StatusBadge';

import { Badge } from 'components/ui/badge';
import { Estimate } from 'services/api/estimates/types';
import { formatAddress } from 'services/api/helpers';
import { Invoice } from 'services/api/invoices/types';
import { Job } from 'services/api/jobs/types';
import { formatDate, formatTime } from 'utils/helpers';
import { useLocale } from 'utils/hooks/useLocale';

type OverviewParts =
  | 'schedule'
  | 'estimate'
  | 'estimate-schedule'
  | 'invoice'
  | 'customer';

export default function WorkRequestOverview({
  workRequest,
  exclude,
  manualWorkRequest,
}: {
  workRequest: Job | Estimate | Invoice;
  exclude?: OverviewParts[];
  manualWorkRequest?: boolean;
}) {
  const includedParts: OverviewParts[] = [
    'schedule',
    'estimate',
    'invoice',
    'customer',
    'estimate-schedule',
  ].filter((v) => !exclude?.some((value) => v === value)) as OverviewParts[];

  const { t } = useLocale();
  const assignedTo = workRequest.jobAssignedTo.length
    ? workRequest.jobAssignedTo
    : workRequest.estimateAssignedTo.length
    ? workRequest.estimateAssignedTo
    : [];

  const isEstimateReadyToSchedule =
    workRequest.estimateRequired && workRequest.progress === 'EstApproved';

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <div className="text-3xl font-semibold text-brand-primary">
          {`#${workRequest.num}`}
          {/* <span
          className={clsx(
            WORK_REQUEST_STATUS_COLORS[workRequest.progress].light.text,
            )}
            >
            {t(`status.${WORK_REQUEST_STATUS_LABELS[workRequest.progress]}`, {
              ns: 'glossary',
          })}
        </span> */}
        </div>
        <StatusBadge size="large" status={workRequest.progress} />
      </div>
      <div className="space-y-6">
        {/* <div>
          <div>
            <Heading className="text-base font-semibold leading-7 text-gray-900">
              {t('Schedule')}
            </Heading>
          </div>
          {!workRequest.jobScheduleDate ? (
            <div>
              <div className="my-2 w-full rounded-lg border border-slate-400 bg-slate-50 p-2.5">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Info
                      className="h-5 w-5 text-slate-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-slate-700">
                      {t('Job not scheduled')}.{' '}
                      <Link
                        to={`/schedule?workRequest=${workRequest.id}`}
                        className="font-medium text-slate-700 underline hover:text-slate-600"
                      >
                        {t('Schedule Now')}
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <dl className="">
              <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="font-medium leading-6 text-gray-900">
                  {t('Starts')}
                </dt>
                <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                  {formatDate(
                    new Date(
                      `${
                        workRequest.jobActualDate ?? workRequest.jobScheduleDate
                      }T00:00:00`,
                    ),
                  )}
                  <span className="mx-1">at</span>
                  {formatTime(
                    new Date(
                      workRequest.jobActualDateTimeStart ??
                        `${workRequest.jobScheduleDate}T${
                          workRequest.jobActualTimeStart ??
                          workRequest.jobTimeStart
                        }`,
                    ),
                  )}
                </dd>
              </div>
              <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="font-medium leading-6 text-gray-900">
                  {t('Ends')}
                </dt>
                <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                  {formatDate(
                    new Date(
                      `${
                        workRequest.jobActualDate ?? workRequest.jobScheduleDate
                      }T00:00:00`,
                    ),
                  )}
                  <span className="mx-1">at</span>
                  {formatTime(
                    new Date(
                      workRequest.jobActualDateTimeStart ??
                        `${workRequest.jobScheduleDate}T${
                          workRequest.jobActualTimeStart ??
                          workRequest.jobTimeStart
                        }`,
                    ),
                  )}
                </dd>
              </div>
              <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="font-medium leading-6 text-gray-900">
                  {t('Scheduled by')}
                </dt>
                <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                  {workRequest.jobScheduledBy}
                </dd>
              </div>
            </dl>
          )}
        </div> */}
        {!workRequest.estimateRequired ? (
          <JobScheduleOverview workRequest={workRequest} manualWorkRequest={manualWorkRequest} />
        ) : // Estimate is approved we show the `Job` schedule details
        workRequest.estimateRequired && isEstimateReadyToSchedule ? (
          <JobScheduleOverview workRequest={workRequest} manualWorkRequest={manualWorkRequest} />
        ) : // Job for estimate is scheduled we show `Job` schedule details
        // We don't check for the progress since it changes
        workRequest.estimateRequired && workRequest.jobScheduleDate ? (
          <JobScheduleOverview workRequest={workRequest} manualWorkRequest={manualWorkRequest} />
        ) : (
          <EstimateScheduleOverview workRequest={workRequest} />
        )}
        {includedParts.find((p) => p === 'estimate') && (
          <EstimateOverview workRequest={workRequest} />
        )}
        {/* {includedParts.find((p) => p === 'estimate-schedule') && (
          <EstimateScheduleOverview workRequest={workRequest} />
        )} */}
        {includedParts.find((p) => p === 'invoice') && (
          <InvoiceOverview workRequest={workRequest} />
        )}

        <hr />
        <div className="space-y-2">
          <Heading className="text-base font-semibold leading-7 text-gray-900">
            {t('Customer Details')}
          </Heading>
          <dl className="space-y-4">
            <div className="flex w-full">
              <dt className="sr-only">{t('Customer Name')}</dt>
              <dd className="text-base font-semibold leading-6 text-gray-900">
                {workRequest.custName}
              </dd>
            </div>
            <div className="flex w-full space-x-4">
              <dt className="flex-none">
                <span className="sr-only">{t('Address')}</span>
                <Navigation
                  className="h-6 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </dt>
              <dd className="text-sm font-medium leading-6 text-gray-900">
                {formatAddress(workRequest.address)}
              </dd>
            </div>
            <div className="flex w-full space-x-4">
              <dt className="flex-none">
                <span className="sr-only">{t('Phone Number')}</span>
                <Phone className="h-6 w-5 text-gray-400" aria-hidden="true" />
              </dt>
              <dd className="text-sm font-medium leading-6 text-gray-900">
                {workRequest.phone}
              </dd>
            </div>
            <div className="flex w-full space-x-4">
              <dt className="flex-none">
                <span className="sr-only">{t('Email Address')}</span>
                <Mail className="h-6 w-5 text-gray-400" aria-hidden="true" />
              </dt>
              <dd className="text-sm font-medium leading-6 text-gray-900">
                {workRequest.email}
              </dd>
            </div>
          </dl>
        </div>

        <div className="space-y-2">
          <Heading className="text-base font-semibold leading-7 text-gray-900">
            {t('Work Details')}
          </Heading>
          <dl>
            <div className="flex w-full">
              <dt className="sr-only">{t('Work type')}</dt>
              <dd className="text-sm font-semibold leading-6 text-gray-900">
                {workRequest.workType}
              </dd>
            </div>
            <div className="flex w-full">
              <dt className="sr-only">{t('Work Description')}</dt>
              <dd className="text-sm leading-6 text-gray-700">
                {workRequest.workTypeDescription}
              </dd>
            </div>
          </dl>
        </div>
        <div className="space-y-2">
          <Heading className="text-base font-semibold leading-7 text-gray-900">
            {t('Assigned')}
          </Heading>
          <dd>
            {assignedTo.length !== 0 ? (
              <ul>
                {assignedTo.map((employee) => (
                  <li
                    key={employee.name}
                    className="text-sm font-medium leading-6 text-gray-900"
                  >
                    {employee.name}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="my-2 w-full rounded-lg border border-gray-400 bg-gray-50 p-2.5">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Info
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700">
                      {t('No employee assigned')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </dd>
        </div>
      </div>
    </div>
  );
}

function InvoiceOverview({
  workRequest,
}: {
  workRequest: Job | Estimate | Invoice;
}) {
  const { t } = useLocale();
  return (
    <div>
      <div className="flex items-center">
        <Heading className="text-base font-semibold leading-7 text-gray-900">
          {t('Invoice')}
        </Heading>
        {workRequest.progress.toLowerCase().includes('invoice') &&
          workRequest.progress === 'Collected' && (
            <StatusBadge status={workRequest.progress} />
          )}
      </div>
      <dl className="">
        <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
          <dt className="font-medium leading-6 text-gray-900">{t('Sent')}</dt>
          <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
            {workRequest.invoiceSent
              ? `${formatDate(
                  new Date(workRequest.invoiceSent),
                )} at ${formatTime(new Date(workRequest.invoiceSent))}`
              : '---'}
          </dd>
        </div>
        <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
          <dt className="font-medium leading-6 text-gray-900">{t('Paid')}</dt>
          <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
            {workRequest.invoicePaid
              ? `${formatDate(
                  new Date(workRequest.invoicePaid),
                )} at ${formatTime(new Date(workRequest.invoicePaid))}`
              : '---'}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function JobScheduleOverview({
  workRequest,
  manualWorkRequest,
}: {
  workRequest: Job | Estimate | Invoice;
  manualWorkRequest?: boolean;
}) {
  const { t } = useLocale();

  return (
    <div>
      <div className="flex items-center space-x-1">
        <Heading className="text-base font-semibold leading-7 text-gray-900">
          {/* {t('Estimate Schedule')} */}
          {t('Schedule')}
        </Heading>
        {workRequest.recurring && <Badge>{t('Recurring')}</Badge>}
      </div>
      {!workRequest.jobScheduleDate ? (
        <div>
          <div className="my-2 w-full rounded-lg border border-slate-400 bg-slate-50 p-2.5">
            <div className="flex">
              <div className="flex-shrink-0">
                <Info className="h-5 w-5 text-slate-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-slate-700">
                  {t('Job not scheduled')}.{' '}
                  <Link
                    to={
                      manualWorkRequest
                        ? `/work-requests/new?scheduleJobId=${workRequest.id}&manual=true`
                        : `/schedule?workRequestId=${workRequest.id}`
                    }
                    className="font-medium text-slate-700 underline hover:text-slate-600"
                  >
                    {t('Schedule Now')}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <dl className="">
          <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium leading-6 text-gray-900">
              {t('Starts')}
            </dt>
            <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
              {workRequest.jobActualDateTimeStart ? (
                <>
                  {formatDate(new Date(workRequest.jobActualDateTimeStart))}
                  <span className="mx-1">at</span>
                  {formatTime(new Date(workRequest.jobActualDateTimeStart))}
                </>
              ) : (
                <>
                  {formatDate(
                    new Date(`${workRequest.jobScheduleDate}T00:00:00`),
                  )}
                  <span className="mx-1">at</span>
                  {formatTime(
                    new Date(
                      `${workRequest.jobScheduleDate}T${workRequest.jobTimeStart}`,
                    ),
                  )}
                </>
              )}
            </dd>
          </div>
          <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium leading-6 text-gray-900">{t('Ends')}</dt>
            <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
              {workRequest.jobActualDateTimeEnd ? (
                <>
                  {formatDate(new Date(workRequest.jobActualDateTimeEnd))}
                  <span className="mx-1">at</span>
                  {formatTime(new Date(workRequest.jobActualDateTimeEnd))}
                </>
              ) : (
                <>
                  {formatDate(
                    new Date(`${workRequest.jobScheduleEndDate}T00:00:00`),
                  )}
                  <span className="mx-1">at</span>
                  {formatTime(
                    new Date(
                      `${workRequest.jobScheduleEndDate}T${workRequest.jobTimeEnd}`,
                    ),
                  )}
                </>
              )}
            </dd>
          </div>
          <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="break-all font-medium leading-6 text-gray-900">
              {t('Scheduled by')}
            </dt>
            <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
              {workRequest.jobScheduledBy}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

function EstimateScheduleOverview({
  workRequest,
}: {
  workRequest: Job | Estimate | Invoice;
}) {
  const { t } = useLocale();

  return (
    <div>
      <div>
        <Heading className="text-base font-semibold leading-7 text-gray-900">
          {/* {t('Estimate Schedule')} */}
          {t('Schedule')}
        </Heading>
      </div>
      {!workRequest.estimateScheduleDate ? (
        <div>
          <div className="my-2 w-full rounded-lg border border-slate-400 bg-slate-50 p-2.5">
            <div className="flex">
              <div className="flex-shrink-0">
                <Info className="h-5 w-5 text-slate-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-slate-700">
                  {t('Estimate not scheduled')}.{' '}
                  <Link
                    to={`/schedule?workRequestId=${workRequest.id}`}
                    className="font-medium text-slate-700 underline hover:text-slate-600"
                  >
                    {t('Schedule Now')}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <dl className="">
          <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium leading-6 text-gray-900">
              {t('Starts')}
            </dt>
            <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
              {formatDate(
                new Date(
                  `${
                    workRequest.estimateActualDate ??
                    workRequest.estimateScheduleDate
                  }T00:00:00`,
                ),
              )}
              <span className="mx-1">at</span>
              {formatTime(
                new Date(
                  workRequest.estimateActualDateTimeStart ??
                    `${workRequest.estimateScheduleDate}T${
                      workRequest.estimateActualTimeStart ??
                      workRequest.estimateTimeStart
                    }`,
                ),
              )}
            </dd>
          </div>
          <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium leading-6 text-gray-900">{t('Ends')}</dt>
            <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
              {formatDate(
                new Date(
                  `${
                    workRequest.estimateActualEndDate ??
                    workRequest.estimateScheduleEndDate
                  }T00:00:00`,
                ),
              )}
              <span className="mx-1">at</span>
              {formatTime(
                new Date(
                  workRequest.estimateActualDateTimeEnd ??
                    `${workRequest.estimateScheduleEndDate}T${
                      workRequest.estimateActualTimeEnd ??
                      workRequest.estimateTimeEnd
                    }`,
                ),
              )}
            </dd>
          </div>
          <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="break-all font-medium leading-6 text-gray-900">
              {t('Scheduled by')}
            </dt>
            <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
              {workRequest.estimateScheduledBy}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

function EstimateOverview({
  workRequest,
}: {
  workRequest: Job | Estimate | Invoice;
}) {
  const { t } = useLocale();

  return (
    <div>
      <div className="flex items-center">
        <Heading className="text-base font-semibold leading-7 text-gray-900">
          {t('Estimate')}
        </Heading>
        {workRequest.estimateRequired &&
          workRequest.progress.toLowerCase().includes('est') && (
            <StatusBadge status={workRequest.progress} />
          )}
      </div>
      {!workRequest.estimateRequired && (
        <div className="my-2 w-full rounded-lg border border-gray-400 bg-gray-50 p-2.5">
          <div className="flex">
            <div className="flex-shrink-0">
              <Info className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-gray-700">
                {t('No estimate required')}
              </p>
            </div>
          </div>
        </div>
      )}
      {workRequest.estimateRequired && (
        <dl className="">
          <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium leading-6 text-gray-900">{t('Sent')}</dt>
            <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
              {workRequest.estimateSent
                ? `${formatDate(
                    new Date(workRequest.estimateSent),
                  )} at ${formatTime(new Date(workRequest.estimateSent))}`
                : '---'}
            </dd>
          </div>
          <div className="py-0.5 text-sm sm:grid sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium leading-6 text-gray-900">
              {t('Approved')}
            </dt>
            <dd className="mt-1 leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
              {workRequest.estimateApproved
                ? `${formatDate(
                    new Date(workRequest.estimateApproved),
                  )} at ${formatTime(new Date(workRequest.estimateApproved))}`
                : '---'}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}
